import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import _ from 'lodash';
import { getSubscription, getSubscriptionStatus, selectUser } from '../../../reducers/user.selectors';
import { selectActiveLicences } from '../../../reducers/licenceModels.selectors';
import useLicenceModel from '../hooks/useLicenceModel';
import useLibraryIpValidation from '../hooks/useLibraryIpValidation';
import AppNavBar from '../../../components/AppLayout/MasterLayout/AppNavBar';
import { AsyncActionStatus } from '../../../actions/common.actionTypes';
import { routes, userType, packageLicenceIds } from '../../../constants';
import useRouter from '../../../utils/useRouter';
import {
  isPaused as checkIfPaused,
  isSuspended as checkIfSuspended
} from '../../../utils/subscription';
import { useLicenceIdFromUrl } from '../hooks/useLicenceIdFromUrl';
import { customSizes, palette, spaces } from '../../../assets/styles/themes/tokens';
import useScreenSize from '../../../utils/useScreenSize';
import LicenceInfo from '../LicenceInfo';
import { DiscountInformation } from '@klab-berlin/api-sdk/lib/types/responses/Billing';
import { PaymentPeriod } from '@klab-berlin/api-sdk/lib/types/common';
import PaymentForm from '../PaymentForm';

export enum PaymentMode {
  PremiumUpgrade,
  PremiumRegistration,
}

interface PaymentPageProps {
  paymentMode: PaymentMode;
}

interface ColumnContainerProps {
  isLargeScreen: boolean;
  dataTestId: string;
  bgcolor: string;
}

const ColumnContainer: React.FC<ColumnContainerProps> = ({ dataTestId, bgcolor, isLargeScreen, children }) => {
  const { paymentInformationContainer } = customSizes;

  return (
    <Box
      data-testid={dataTestId}
      flex={1}
      bgcolor={bgcolor}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        paddingY: isLargeScreen ? paymentInformationContainer.paddingY : spaces.xxl,
      }}
    >
      <Box
        sx={{ 
          width: isLargeScreen ? 
            paymentInformationContainer.width.desktop : 
            paymentInformationContainer.width.mobile 
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

const PaymentPage: React.FC<PaymentPageProps> = (props) => {
  const { t } = useTranslation();
  const { isLargeScreen } = useScreenSize();
  const { paymentMode } = props;
  const { match: { location: { query } }, router } = useRouter();
  const { meinUnterricht360ForDocumentUsers } = packageLicenceIds;
  const [promoCode, setPromoCode] = useState<string>(query.promoCode || '');
  const [promoCodeDiscountInfo, setPromoCodeDiscountInfo] = useState<DiscountInformation>();
  const paymentRhythms = ['monthly', 'annually'] as PaymentPeriod[];
  const [selectedPaymentRhythm, setSelectedPaymentRhythm] = useState<PaymentPeriod>(paymentRhythms[0]);
  const user = useSelector(selectUser);
  const subscription = useSelector(getSubscription);
  const subscriptionStatus = useSelector(getSubscriptionStatus);
  const licenceModels = useSelector(state => selectActiveLicences(state));
  const isPaused = checkIfPaused(subscription);
  const isSuspended = checkIfSuspended(subscription);
  const urlLibrary = query.library;
  const urlRedirect = query.optin_redirect;
  const urlLicenceId = useLicenceIdFromUrl();
  const { hasValidLibraryIp, isLibraryReady } = useLibraryIpValidation({ urlLibrary });
  const { selectedLicenceModel, toggleSelectedLicenceModel } = useLicenceModel({
    licenceModels,
    urlLicenceId,
    isLibraryReady,
    hasValidLibraryIp
  });

  useEffect(() => {
    if (user) {
      const { isTrial, isKombiUser } = user;
      const { licenceModelId: selectedLicenceId } = selectedLicenceModel;
      const isDocumentUser = user?.type === userType.document;
      const isUserTypeAllowed = user?.type && selectedLicenceModel?.allowedUserTypes?.includes(user.type);
      const isLicenceModelSelected = Number(subscription?.licence?.licenceModelId) === selectedLicenceId;
      const isPremiumRegistration = PaymentMode.PremiumRegistration === paymentMode;
      const isNotAddOnSubscriptionForDocumentUser = 
        isDocumentUser && selectedLicenceModel?.licenceModelId !== meinUnterricht360ForDocumentUsers;

      if (isTrial
        || isKombiUser
        || user.canUpgrade === false
        || !isUserTypeAllowed 
        || isLicenceModelSelected 
        || isPremiumRegistration 
        || isNotAddOnSubscriptionForDocumentUser
      ) {
        router.push(`/${routes.upgradePackageSelection.root}`);
      }
    }  
  }, [selectedLicenceModel]);

  const isLicencesDataReady = () => !_.isEmpty(selectedLicenceModel);

  if (paymentMode === PaymentMode.PremiumUpgrade){
    if (!subscriptionStatus || ![AsyncActionStatus.READY, AsyncActionStatus.NOTFOUND].includes(subscriptionStatus)){
      return <></>;
    }

    if (isPaused || isSuspended) {
      router.push(`/${routes.upgradePackageSelection.root}`);
    }
  }

  return (
    <Box 
      data-testid='payment-page-container'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}
    >
      <AppNavBar title={t('Payment')}/>
      {isLicencesDataReady() && (
        <Box
          data-testid='payment-page-content'
          sx={{
            display: 'flex',
            flexDirection: isLargeScreen ? 'row' : 'column',
            flex: 1,
          }}
        >
          <ColumnContainer 
            dataTestId='payment-page-licence-info'
            bgcolor={palette.background.paperElevation0}
            isLargeScreen={isLargeScreen}
          >
            <LicenceInfo
              licence={selectedLicenceModel}
              toggleSelectedLicenceModel={toggleSelectedLicenceModel}
              library={urlLibrary}
              promoCode={promoCode}
              setPromoCode={setPromoCode}
              promoCodeDiscountInfo={promoCodeDiscountInfo}
              setPromoCodeDiscountInfo={setPromoCodeDiscountInfo}
              setSelectedPaymentRhythm={setSelectedPaymentRhythm}
              selectedPaymentRhythm={selectedPaymentRhythm}
              paymentRhythms={paymentRhythms}
            />
          </ColumnContainer>
          <ColumnContainer 
            dataTestId='payment-page-form'
            bgcolor={palette.background.paperElevation2}
            isLargeScreen={isLargeScreen}
          >
            <PaymentForm
              licence={selectedLicenceModel}
              library={urlLibrary}
              paymentMode={paymentMode}
              promoCode={promoCode}
              paymentPeriod={selectedPaymentRhythm}
              promoCodeDiscountInfo={promoCodeDiscountInfo}
              redirect={urlRedirect}
            />
          </ColumnContainer>
        </Box> 
      )}
    </Box>
  );
};

export default PaymentPage;
